import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.echomemoir.ai/api';

const GiftPurchase = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled');

  const [selectedTier, setSelectedTier] = useState('legacy_keeper');
  const [purchaserName, setPurchaserName] = useState('');
  const [purchaserEmail, setPurchaserEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const tiers = {
    legacy_keeper: {
      name: 'Legacy Keeper',
      price: '$96',
      emoji: '🌳',
      features: ['50 stories & 3 family circles', 'Voice cloning', 'PDF exports & memory books', '5 GB storage', 'Share links & QR codes']
    },
    legacy_vault: {
      name: 'Legacy Vault',
      price: '$288',
      emoji: '🏛️',
      features: ['Unlimited stories & family circles', 'Voice cloning', 'PDF exports & memory books', '50 GB storage', 'Priority support', 'Share links & QR codes']
    }
  };

  const handleSubmit = async () => {
    setError('');

    if (!purchaserName.trim() || !purchaserEmail.trim()) {
      setError('Your name and email are required.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(purchaserEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/billing/gift-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: selectedTier,
          purchaserName: purchaserName.trim(),
          purchaserEmail: purchaserEmail.trim(),
          recipientName: recipientName.trim() || null,
          recipientEmail: recipientEmail.trim() || null,
          giftMessage: giftMessage.trim() || null
        })
      });

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to create checkout. Please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      console.error('Gift checkout error:', err);
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const selected = tiers[selectedTier];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#08060e',
      color: '#fff',
      padding: '40px 20px 80px'
    }}>
      <div style={{ maxWidth: '560px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <button
            onClick={() => navigate('/')}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '14px', cursor: 'pointer', marginBottom: '20px', display: 'inline-block' }}
          >
            ← Back
          </button>
          <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
            🎁 Gift a Subscription
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', lineHeight: '1.6' }}>
            Give the gift of preserved memories. One year of EchoMemoir for someone you love.
          </p>
        </div>

        {cancelled && (
          <div style={{
            background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)',
            borderRadius: '10px', padding: '12px 16px', marginBottom: '20px',
            color: '#fbbf24', fontSize: '14px'
          }}>
            Checkout was cancelled. You can try again below.
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '10px', padding: '12px 16px', marginBottom: '20px',
            color: '#fca5a5', fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Plan selector */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '10px' }}>
            Choose a plan
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            {Object.entries(tiers).map(([key, tier]) => (
              <div
                key={key}
                onClick={() => setSelectedTier(key)}
                style={{
                  flex: 1, padding: '16px', borderRadius: '12px', cursor: 'pointer',
                  background: selectedTier === key ? 'rgba(139,92,246,0.1)' : 'rgba(255,255,255,0.03)',
                  border: `2px solid ${selectedTier === key ? 'rgba(139,92,246,0.5)' : 'rgba(255,255,255,0.08)'}`,
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>{tier.emoji}</div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#fff', marginBottom: '4px' }}>{tier.name}</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#fbbf24' }}>{tier.price}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>/ 1 year</div>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div style={{
          background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
          borderRadius: '12px', padding: '16px', marginBottom: '24px'
        }}>
          <p style={{ color: '#a78bfa', fontSize: '12px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {selected.emoji} {selected.name} includes
          </p>
          {selected.features.map((f, i) => (
            <p key={i} style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px', lineHeight: '2', margin: 0 }}>
              ✓ {f}
            </p>
          ))}
        </div>

        {/* Your info */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#fff', marginBottom: '12px' }}>Your Info</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="text" value={purchaserName} onChange={(e) => setPurchaserName(e.target.value)}
              placeholder="Your name *"
              style={inputStyle}
            />
            <input
              type="email" value={purchaserEmail} onChange={(e) => setPurchaserEmail(e.target.value)}
              placeholder="Your email *"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Recipient info */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#fff', marginBottom: '4px' }}>Recipient (optional)</h3>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px', marginBottom: '12px' }}>
            If provided, we'll email them the gift code directly.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="text" value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Recipient's name"
              style={inputStyle}
            />
            <input
              type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="Recipient's email"
              style={inputStyle}
            />
            <textarea
              value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)}
              placeholder="Add a personal message..."
              rows={3}
              maxLength={500}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
            />
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%', padding: '16px', borderRadius: '12px', border: 'none',
            background: submitting ? 'rgba(139,92,246,0.3)' : 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
            color: '#fff', fontSize: '16px', fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {submitting ? 'Redirecting to checkout...' : `🎁 Purchase Gift — ${selected.price}`}
        </button>

        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', textAlign: 'center', marginTop: '16px' }}>
          Secure payment via Stripe. The gift code will be emailed after purchase.
        </p>
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%', padding: '12px 14px', borderRadius: '10px',
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
  color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box'
};

export default GiftPurchase;
